import { useCallback, useMemo, useState } from "preact/hooks";
import { Calendar, type DayMarker } from "./components/Calendar";
import { TimeInput } from "./components/TimeInput";
import { Watched } from "./components/Watched";
import { WorldMap } from "./components/WorldMap";
import { ZonePicker } from "./components/ZonePicker";
import { getDetectConsent, openedWithZone, setDetectConsent, todayInZone, useAppState, type DetectConsent } from "./state";
import { db, cityName, dataVersions, guessLocalZone, trackedZones } from "./tz";
import { collectChanges } from "./tz/changes";
import { formatDate, formatOffset, formatTime, fromCivil, isoDate, toCivil, type TimeFormat } from "./tz/civil";

const CHANGE_WINDOW = 7 * 86400;

export function App() {
  const [state, update] = useAppState();
  const [consent, setConsentState] = useState<DetectConsent>(getDetectConsent);
  const askConsent = consent === null && !openedWithZone;
  const chooseDetect = (v: "yes" | "no") => {
    setDetectConsent(v);
    setConsentState(v);
    if (v === "yes") {
      const zone = guessLocalZone();
      update({ zone, ...todayInZone(zone) });
    }
  };
  const zone = db.zone(state.zone);
  const local = fromCivil(state.year, state.month, state.day, state.hour, state.minute);
  const resolution = useMemo(() => zone.resolveLocal(local), [zone, local]);
  const instant = resolution.utc;
  const srcType = zone.typeAt(instant);
  const srcCivil = toCivil(instant + srcType.utoff);
  const sourceDay = isoDate(srcCivil);
  const utcCivil = toCivil(instant);

  const nextHere = useMemo(() => zone.nextTransition(instant), [zone, instant]);
  const prevHere = useMemo(() => zone.prevTransition(instant), [zone, instant]);

  /** The first source-zone date on which the current clock time falls after `utc`. */
  const landingDay = useCallback(
    (utc: number) => {
      const c = toCivil(utc + zone.offsetAt(utc));
      let candidate = fromCivil(c.year, c.month, c.day, state.hour, state.minute);
      if (zone.resolveLocal(candidate).utc < utc) candidate += 86400;
      return candidate;
    },
    [zone, state.hour, state.minute],
  );

  /** Set date (and optionally time) so the clock reads the source-zone wall time of `utc`. */
  const jumpTo = useCallback(
    (utc: number, exact: boolean) => {
      if (exact) {
        const c = toCivil(utc + zone.offsetAt(utc));
        update({ year: c.year, month: c.month, day: c.day, hour: c.hour, minute: c.minute });
        return;
      }
      const c = toCivil(landingDay(utc));
      update({ year: c.year, month: c.month, day: c.day });
    },
    [zone, landingDay, update],
  );

  /** Move to the nearest day, strictly after (dir=1) or before (dir=-1) the current one, on which some zone's clocks change. */
  const jumpAnywhere = (dir: 1 | -1) => {
    const today = fromCivil(state.year, state.month, state.day);
    for (const g of collectChanges(instant, dir, [state.zone], 60)) {
      const day = landingDay(g.at);
      const dayStart = fromCivil(toCivil(day).year, toCivil(day).month, toCivil(day).day);
      if (dir > 0 ? dayStart > today : dayStart < today) {
        const c = toCivil(day);
        update({ year: c.year, month: c.month, day: c.day });
        return;
      }
    }
  };

  const markers = useCallback(
    (year: number, month: number): Map<string, DayMarker> => {
      const from = fromCivil(year, month, 1) - 2 * 86400;
      const to = fromCivil(year, month + 1, 1) + 2 * 86400;
      const m = new Map<string, DayMarker>();
      const selectedCanon = db.canonical(state.zone);
      for (const id of trackedZones([state.zone])) {
        const z = db.zone(id);
        for (const tr of z.transitionsBetween(from, to)) {
          const day = isoDate(toCivil(tr.at + tr.before.utoff));
          let mk = m.get(day);
          if (!mk) {
            mk = { count: 0, selected: false, zones: [] };
            m.set(day, mk);
          }
          mk.count++;
          mk.zones.push(cityName(id));
          if (id === selectedCanon) mk.selected = true;
        }
      }
      return m;
    },
    [state.zone],
  );

  const setNow = () => update(todayInZone(state.zone));
  const toggleWatch = (id: string) => {
    const c = db.canonical(id);
    if (!c) return;
    update({ watch: state.watch.includes(c) ? state.watch.filter((w) => w !== c) : [...state.watch, c] });
  };
  const setFmt = (fmt: TimeFormat) => update({ fmt });

  return (
    <div class="layout">
      <header class="top">
        <h1>
          timezoo <span class="tagline">one clock time, every clock in the world</span>
        </h1>
        <div class="seg" role="radiogroup" aria-label="Time format">
          <button type="button" role="radio" aria-checked={state.fmt === "24h"} class={state.fmt === "24h" ? "on" : ""} onClick={() => setFmt("24h")}>24h</button>
          <button type="button" role="radio" aria-checked={state.fmt === "12h"} class={state.fmt === "12h" ? "on" : ""} onClick={() => setFmt("12h")}>am/pm</button>
        </div>
      </header>

      {askConsent && (
        <section class="consent" role="dialog" aria-labelledby="consent-title">
          <div>
            <strong id="consent-title">Use your device's time zone?</strong>
            <p class="muted">
              Detecting your time zone shows your current local time and region on screen, which hints at where you are. If you are
              streaming or sharing your screen you may prefer to start from UTC and pick a place yourself. Nothing is sent anywhere either way.
            </p>
          </div>
          <div class="consent-actions">
            <button type="button" class="btn" onClick={() => chooseDetect("no")}>Keep it private</button>
            <button type="button" class="btn primary" onClick={() => chooseDetect("yes")}>Use my time zone</button>
          </div>
        </section>
      )}

      <section class="controls">
        <label class="field grow">
          <span>Where</span>
          <ZonePicker value={state.zone} onChange={(id) => update({ zone: id })} />
        </label>
        <label class="field">
          <span>Date</span>
          <input
            type="date"
            value={`${state.year}-${String(state.month).padStart(2, "0")}-${String(state.day).padStart(2, "0")}`}
            onInput={(e) => {
              const v = (e.target as HTMLInputElement).valueAsDate;
              if (v) update({ year: v.getUTCFullYear(), month: v.getUTCMonth() + 1, day: v.getUTCDate() });
            }}
          />
        </label>
        <label class="field">
          <span>Clock time</span>
          <TimeInput hour={state.hour} minute={state.minute} fmt={state.fmt} onChange={(hour, minute) => update({ hour, minute })} />
        </label>
        <button type="button" class="btn" onClick={setNow} title="Set to the current time in the selected zone">Now</button>
      </section>

      <section class="summary">
        <div>
          <strong>{formatTime(srcCivil, state.fmt)}</strong> {formatDate(srcCivil)} in <strong>{cityName(zone.id)}</strong>{" "}
          <span class="muted">({srcType.abbr}, UTC{formatOffset(srcType.utoff)}{srcType.isdst ? ", daylight time" : ""})</span>
          {" "}= <strong>{formatTime(utcCivil, state.fmt)}</strong> {formatDate(utcCivil)} UTC
        </div>
        {resolution.kind === "gap" && (
          <div class="warn">
            {formatTime(toCivil(local), state.fmt)} does not exist in {cityName(zone.id)} on this day (clocks jump forward); showing {formatTime(srcCivil, state.fmt)} instead.
          </div>
        )}
        {resolution.kind === "ambiguous" && (
          <div class="warn">
            {formatTime(toCivil(local), state.fmt)} happens twice in {cityName(zone.id)} on this day (clocks go back); showing the first occurrence ({srcType.abbr}).
          </div>
        )}
        <div class="muted here-changes">
          {prevHere && (
            <span>
              Last change here: <button type="button" class="link" onClick={() => jumpTo(prevHere.at, true)}>{formatDate(toCivil(prevHere.at + prevHere.before.utoff))}</button> ({prevHere.before.abbr} → {prevHere.after.abbr})
            </span>
          )}
          {nextHere ? (
            <span>
              Next change here: <button type="button" class="link" onClick={() => jumpTo(nextHere.at, true)}>{formatDate(toCivil(nextHere.at + nextHere.before.utoff))}</button>{" "}
              {formatTime(toCivil(nextHere.at + nextHere.before.utoff), state.fmt)} → {formatTime(toCivil(nextHere.at + nextHere.after.utoff), state.fmt)} ({nextHere.before.abbr} → {nextHere.after.abbr})
            </span>
          ) : (
            <span>No scheduled clock changes in {cityName(zone.id)}.</span>
          )}
        </div>
      </section>

      <main class="main">
        <section class="map-section">
          <WorldMap
            instant={instant}
            sourceDay={sourceDay}
            selected={state.zone}
            fmt={state.fmt}
            changeWindow={CHANGE_WINDOW}
            watched={state.watch}
            onToggle={toggleWatch}
          />
          <p class="hint muted">
            Colour is the local time of day; places that keep the same clock this year are drawn as one region. Hatched regions change their clocks within a week of this moment. Click a place to add it to the list below; scroll to zoom, drag to pan, double-click to reset.
          </p>
          <Watched
            instant={instant}
            sourceDay={sourceDay}
            zones={state.watch}
            fmt={state.fmt}
            onRemove={toggleWatch}
            onClear={() => update({ watch: [] })}
            onReference={(id) => update({ zone: id })}
          />
        </section>

        <aside class="side">
          <Calendar year={state.year} month={state.month} day={state.day} markers={markers} onSelect={(year, month, day) => update({ year, month, day })} />
          <div class="jump-row">
            <button type="button" class="btn" onClick={() => jumpAnywhere(-1)} title="Jump to the previous clock change anywhere in the world">‹ Previous change</button>
            <button type="button" class="btn" onClick={() => update(todayInZone(state.zone))}>Today</button>
            <button type="button" class="btn" onClick={() => jumpAnywhere(1)} title="Jump to the next clock change anywhere in the world">Next change ›</button>
          </div>
        </aside>
      </main>

      <footer class="foot muted">
        tz database {dataVersions.tzdata} · boundaries {dataVersions.map} from{" "}
        <a href="https://github.com/evansiroky/timezone-boundary-builder">timezone-boundary-builder</a> ·{" "}
        <a href="https://github.com/sugoijan/timezoo">source</a> · device time zone:{" "}
        {consent === "yes" ? (
          <>
            used · <button type="button" class="link" onClick={() => chooseDetect("no")}>stop using it</button>
          </>
        ) : (
          <>
            not used · <button type="button" class="link" onClick={() => chooseDetect("yes")}>use it</button>
          </>
        )}
      </footer>
    </div>
  );
}
